import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Zap, Save, Flag, Gavel, RotateCcw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { gruppoColor } from "@/lib/race";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const MANCHE_TABS = [
  { key: "p1", label: "Prove M1", day: "Sabato" },
  { key: "p2", label: "Prove M2", day: "Sabato" },
  { key: "g1", label: "Gara M1", day: "Domenica" },
  { key: "g2", label: "Gara M2", day: "Domenica" },
];

const STATUS_OPTIONS = {
  p1: [ ["ok", "OK"], ["dns", "DNS -4 (Saltata Manche)"], ["dnf", "DNF -5 (Fuori Strada)"] ],
  p2: [ ["ok", "OK"], ["dns", "DNS -4 (Saltata Manche)"], ["dnf", "DNF -5 (Fuori Strada)"] ],
  g1: [ ["ok", "OK"], ["dnf", "RITIRATO -10"], ["dsq", "SQUALIFICATO -10"] ],
  g2: [ ["ok", "OK"], ["dnf", "RITIRATO -10"], ["dsq", "SQUALIFICATO -10"] ],
};

export default function RaceTab() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("p1");
  const [rows, setRows] = useState({});
  const [publishing, setPublishing] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [varForm, setVarForm] = useState({ pilot_id: "", seconds: "", squalifica: false, note: "" });

  const resetResults = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/results/reset");
      toast.success(`Risultati azzerati con successo (${data.risultati_rimossi} record rimossi)`);
      setRows({});
      setResetOpen(false);
      qc.invalidateQueries();
    } catch (e) {
      toast.error(errMsg(e, "Reset fallito"));
    } finally {
      setResetting(false);
    }
  };

  const applyVar = async () => {
    if (!varForm.pilot_id) {
      toast.error("Seleziona un pilota");
      return;
    }
    try {
      await api.post("/admin/penalty", {
        pilot_id: varForm.pilot_id,
        seconds: parseFloat(varForm.seconds) || 0,
        squalifica: varForm.squalifica,
        note: varForm.note,
      });
      toast.success("Penalità VAR applicata e classifiche ricalcolate retroattivamente");
      setVarForm({ pilot_id: "", seconds: "", squalifica: false, note: "" });
      qc.invalidateQueries();
    } catch (e) {
      toast.error(errMsg(e, "Penalità fallita"));
    }
  };

  const { data: pilots = [] } = useQuery({
    queryKey: ["pilots", "", "", ""],
    queryFn: async () => (await api.get("/pilots")).data,
    refetchInterval: 15000,
  });

  const { data: settings } = useQuery({
    queryKey: ["settings-public"],
    queryFn: async () => (await api.get("/settings/public")).data,
  });
  const garaFinita = settings?.race?.gara_finita === true;

  useEffect(() => {
    const init = {};
    pilots.forEach((p) => {
      const r = p.risultato || {};
      init[p.id] = {
        p1: { time: r.p1_time != null ? String(r.p1_time) : "", status: r.p1_status === "nd" ? "ok" : r.p1_status || "ok" },
        p2: { time: r.p2_time != null ? String(r.p2_time) : "", status: r.p2_status === "nd" ? "ok" : r.p2_status || "ok" },
        g1: { time: r.g1_time != null ? String(r.g1_time) : "", status: r.g1_status === "nd" ? "ok" : r.g1_status || "ok" },
        g2: { time: r.g2_time != null ? String(r.g2_time) : "", status: r.g2_status === "nd" ? "ok" : r.g2_status || "ok" },
      };
    });
    setRows((prev) => (Object.keys(prev).length ? prev : init));
  }, [pilots]);

  const setRow = (id, k, v) =>
    setRows((prev) => ({ ...prev, [id]: { ...prev[id], [tab]: { ...prev[id]?.[tab], [k]: v } } }));

  const saveRow = async (p) => {
    const row = rows[p.id]?.[tab];
    try {
      await api.put(`/admin/results/${p.id}`, { manche: tab, time: row.time, status: row.status });
      toast.success(`${p.cognome} — ${MANCHE_TABS.find((t) => t.key === tab).label} registrata`);
      qc.invalidateQueries({ queryKey: ["pilots"] });
      qc.invalidateQueries({ queryKey: ["results"] });
    } catch (e) {
      toast.error(errMsg(e, "Salvataggio fallito"));
    }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post("/admin/calculate");
      toast.success(`Punteggi pubblicati in diretta: ${data.piloti_aggiornati} piloti, ${data.squadre_aggiornate} squadre aggiornate`);
      qc.invalidateQueries();
    } catch (e) {
      toast.error(errMsg(e, "Calcolo fallito"));
    } finally {
      setPublishing(false);
    }
  };

  const toggleFineGara = async () => {
    try {
      await api.put("/admin/settings", {
        active: settings?.maintenance?.active || false,
        message: settings?.maintenance?.message || "",
        reopen_at: settings?.maintenance?.reopen_at || null,
        gara_finita: !garaFinita,
      });
      toast.success(garaFinita ? "Classifiche di nuovo UFFICIOSE" : "FINE COMPETIZIONE decretata: classifiche UFFICIALI");
      qc.invalidateQueries({ queryKey: ["settings-public"] });
    } catch (e) {
      toast.error(errMsg(e, "Operazione fallita"));
    }
  };

  const timeCls =
    "w-24 rounded-lg bg-[#121214] border border-[#3F3F4C] px-2 py-1.5 font-time text-sm text-white focus:outline-none focus:border-[#FF1E27] transition-colors";

  const activeTab = MANCHE_TABS.find((t) => t.key === tab);

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 rounded-xl border border-[#FFB703]/40 bg-[#FFB703]/5 p-4">
        <p className="text-xs text-gray-400 font-body-sport max-w-md">
          Inserisci i tempi in formato <span className="font-time text-[#FFB703]">m:ss.cc</span> (es. 1:42.35)
          o secondi, separati per manche. Poi pubblica per ricalcolare i punteggi del weekend.
        </p>
        <div className="flex flex-wrap gap-2 w-full lg:w-auto">
          <button
            data-testid="publish-scores-button"
            onClick={publish}
            disabled={publishing}
            className="flex items-center justify-center gap-2 rounded-lg bg-[#FFB703] hover:bg-[#F5A623] disabled:opacity-50 transition-colors px-5 py-2.5 min-h-[44px] font-racing font-bold text-xs tracking-widest text-black uppercase w-full sm:w-auto"
          >
            <Zap className="w-4 h-4" />
            {publishing ? "Pubblicazione..." : "Pubblica Punti in Diretta"}
          </button>
          <button
            data-testid="recalc-all-button"
            onClick={publish}
            disabled={publishing}
            className="flex items-center justify-center gap-2 rounded-lg border border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/10 disabled:opacity-50 transition-colors px-5 py-2.5 min-h-[44px] font-body-sport font-bold text-xs tracking-widest uppercase w-full sm:w-auto"
          >
            Ricalcola Tutte le Classifiche
          </button>
          <button
            data-testid="decreto-fine-gara"
            onClick={toggleFineGara}
            className={`flex items-center justify-center gap-2 rounded-lg border px-5 py-2.5 min-h-[44px] font-racing font-bold text-xs tracking-widest uppercase transition-colors w-full sm:w-auto ${
              garaFinita
                ? "border-[#22C55E] bg-[#22C55E]/15 text-[#22C55E]"
                : "border-[#FF1E27] text-[#FF1E27] hover:bg-[#FF1E27]/10"
            }`}
          >
            <Flag className="w-4 h-4" />
            {garaFinita ? "Gara Decretata ✓ (Annulla)" : "Decreta Fine Competizione"}
          </button>
          <button
            data-testid="reset-results-button"
            onClick={() => setResetOpen(true)}
            className="flex items-center justify-center gap-2 rounded-lg border border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors px-5 py-2.5 min-h-[44px] font-racing font-bold text-xs tracking-widest uppercase w-full sm:w-auto"
          >
            <RotateCcw className="w-4 h-4" />
            Azzera Risultati Gara
          </button>
        </div>
      </div>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent className="bg-[#1A1A1E] border-[#3F3F4C]" data-testid="reset-results-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-racing uppercase tracking-wide text-white">
              Azzera tutti i risultati?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400 font-body-sport">
              Sei sicuro di voler azzerare tutti i tempi e i risultati inseriti per questa gara?
              Questa azione cancellerà anche i punti provvisori calcolati per gli utenti e riporterà
              lo stato della gara a "In attesa dei risultati". Le penalità VAR configurate restano salvate.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="reset-results-cancel"
              className="bg-transparent border-[#3F3F4C] text-gray-300 hover:bg-[#242429] hover:text-white"
            >
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="reset-results-confirm"
              onClick={resetResults}
              disabled={resetting}
              className="bg-[#EF4444] hover:bg-[#DC2626] text-white font-bold"
            >
              {resetting ? "Azzeramento..." : "Sì, azzera tutto"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex flex-wrap gap-1 rounded-xl border border-[#3F3F4C] bg-[#1A1A1E] p-1">
        {MANCHE_TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`race-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex-1 min-w-[120px] rounded-lg px-3 py-2.5 min-h-[44px] font-body-sport font-bold text-xs tracking-widest uppercase transition-colors ${
              tab === t.key ? "bg-[#FF1E27] text-white" : "text-gray-400 hover:text-white"
            }`}
          >
            <span className="block text-[8px] opacity-70">{t.day}</span>
            {t.label}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-[#3F3F4C] bg-[#1A1A1E] overflow-hidden">
        <div className="hidden md:grid grid-cols-[3rem_1fr_8rem_14rem_5.5rem] gap-2 px-4 py-3 border-b border-[#3F3F4C] font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
          <span>#</span><span>Pilota</span><span>Tempo {activeTab.label}</span><span>Stato</span><span></span>
        </div>
        {pilots.map((p, i) => {
          const row = rows[p.id]?.[tab] || { time: "", status: "ok" };
          return (
            <div
              key={p.id}
              data-testid={`race-entry-${p.id}`}
              className={`grid grid-cols-2 md:grid-cols-[3rem_1fr_8rem_14rem_5.5rem] gap-2 items-center px-4 py-3 border-b border-[#242429] ${
                i % 2 === 0 ? "bg-[#1A1A1E]" : "bg-[#141416]"
              }`}
            >
              <span className="font-time font-bold" style={{ color: gruppoColor(p.gruppo) }}>{p.numero}</span>
              <div className="min-w-0">
                <p className="font-body-sport font-semibold text-white text-sm truncate">{p.cognome}</p>
                <p className="text-[10px] text-gray-500">{p.gruppo}</p>
              </div>
              <input
                data-testid={`race-time-${tab}-${p.id}`}
                inputMode="decimal"
                className={timeCls}
                placeholder="m:ss.cc"
                value={row.time}
                onChange={(e) => setRow(p.id, "time", e.target.value)}
              />
              <select
                data-testid={`race-status-${tab}-${p.id}`}
                className="rounded-lg bg-[#121214] border border-[#3F3F4C] px-2 py-1.5 text-xs text-white font-body-sport focus:outline-none focus:border-[#FF1E27]"
                value={row.status}
                onChange={(e) => setRow(p.id, "status", e.target.value)}
              >
                {STATUS_OPTIONS[tab].map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              <button
                data-testid={`race-save-${tab}-${p.id}`}
                onClick={() => saveRow(p)}
                className="flex items-center justify-center gap-1 rounded-lg border border-[#3F3F4C] text-gray-300 hover:text-[#22C55E] hover:border-[#22C55E] transition-colors py-1.5 min-h-[36px] font-body-sport text-[10px] font-bold tracking-widest uppercase"
              >
                <Save className="w-3.5 h-3.5" /> Salva
              </button>
            </div>
          );
        })}
      </div>

      <div className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5 space-y-4" data-testid="var-section">
        <h3 className="font-body-sport font-bold text-white text-sm uppercase tracking-widest flex items-center gap-2">
          <Gavel className="w-4 h-4 text-[#FF1E27]" /> VAR Virtuale — Penalità Ufficiali dei Commissari
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <select
            data-testid="var-pilot-select"
            className="rounded-lg bg-[#121214] border border-[#3F3F4C] px-3 py-2.5 text-sm text-white font-body-sport focus:outline-none focus:border-[#FF1E27]"
            value={varForm.pilot_id}
            onChange={(e) => setVarForm({ ...varForm, pilot_id: e.target.value })}
          >
            <option value="">Seleziona pilota...</option>
            {pilots.map((p) => (
              <option key={p.id} value={p.id}>
                #{p.numero} {p.cognome} ({p.gruppo})
              </option>
            ))}
          </select>
          <div className="flex gap-1.5">
            <input
              data-testid="var-seconds-input"
              inputMode="decimal"
              className="flex-1 min-w-0 rounded-lg bg-[#121214] border border-[#3F3F4C] px-3 py-2.5 font-time text-sm text-white focus:outline-none focus:border-[#FF1E27]"
              placeholder="Secondi (es. 5)"
              value={varForm.seconds}
              onChange={(e) => setVarForm({ ...varForm, seconds: e.target.value })}
            />
            {[5, 10].map((v) => (
              <button
                key={v}
                data-testid={`var-quick-${v}`}
                onClick={() => setVarForm({ ...varForm, seconds: String(v) })}
                className="shrink-0 rounded-lg border border-[#3F3F4C] px-2.5 font-time text-xs text-[#FFB703] hover:border-[#FFB703] transition-colors"
              >
                +{v}s
              </button>
            ))}
          </div>
          <input
            data-testid="var-note-input"
            className="rounded-lg bg-[#121214] border border-[#3F3F4C] px-3 py-2.5 text-sm text-white placeholder:text-gray-600 font-body-sport focus:outline-none focus:border-[#FF1E27]"
            placeholder="Nota (es. taglio chicane)"
            value={varForm.note}
            onChange={(e) => setVarForm({ ...varForm, note: e.target.value })}
          />
          <label className="flex items-center gap-2 text-xs text-gray-300 font-body-sport cursor-pointer">
            <input
              type="checkbox"
              data-testid="var-dsq-checkbox"
              checked={varForm.squalifica}
              onChange={(e) => setVarForm({ ...varForm, squalifica: e.target.checked })}
              className="accent-[#FF1E27] w-4 h-4"
            />
            Squalifica d'ufficio (DSQ)
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="var-apply-button"
            onClick={applyVar}
            className="flex items-center gap-2 rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] transition-colors px-5 py-2.5 min-h-[44px] font-racing font-bold text-xs tracking-widest text-white uppercase"
          >
            <Gavel className="w-4 h-4" /> Applica Penalità e Ricalcola
          </button>
          <button
            data-testid="var-reset-button"
            onClick={async () => {
              if (!varForm.pilot_id) {
                toast.error("Seleziona un pilota");
                return;
              }
              try {
                await api.post("/admin/penalty", { pilot_id: varForm.pilot_id, reset: true });
                toast.success("Penalità azzerata e classifiche ricalcolate");
                qc.invalidateQueries();
              } catch (e) {
                toast.error(errMsg(e, "Reset fallito"));
              }
            }}
            className="flex items-center gap-2 rounded-lg border border-[#3F3F4C] text-gray-300 hover:border-[#FFB703] hover:text-[#FFB703] transition-colors px-5 py-2.5 min-h-[44px] font-body-sport font-bold text-xs tracking-widest uppercase"
          >
            Azzera Penalità
          </button>
        </div>
      </div>
    </div>
  );
}
